import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { track } from "../lib/track";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [state, setState] = useState({ status: "checking", order: null });
  const { clear } = useCart();
  const polled = useRef(false);

  useEffect(() => {
    if (!session_id || polled.current) return;
    polled.current = true;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${session_id}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", order: data.order });
          track("purchase", { order_id: data.order?.order_id, total: data.order?.total });
          clear();
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", order: data.order });
          return;
        }
        if (attempts++ < 8) setTimeout(poll, 2000);
        else setState({ status: "timeout", order: data.order });
      } catch {
        setState({ status: "error", order: null });
      }
    };
    poll();
    // eslint-disable-next-line
  }, [session_id]);

  const icon = {
    checking: <Clock className="w-12 h-12 text-sage animate-pulse" strokeWidth={1.2} />,
    paid: <CheckCircle2 className="w-12 h-12 text-sage" strokeWidth={1.2} />,
    expired: <XCircle className="w-12 h-12 text-terracotta" strokeWidth={1.2} />,
    timeout: <Clock className="w-12 h-12 text-earth" strokeWidth={1.2} />,
    error: <XCircle className="w-12 h-12 text-terracotta" strokeWidth={1.2} />,
  }[state.status];

  const headline = {
    checking: "Confirming your order…",
    paid: "Thank you. Your seeds are on the way.",
    expired: "Payment session expired.",
    timeout: "Still processing. Check your email.",
    error: "Something went wrong.",
  }[state.status];

  return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="checkout-success">
      <div className="flex justify-center mb-8">{icon}</div>
      <h1 className="font-display text-4xl sm:text-5xl text-darkRoast">{headline}</h1>
      {state.status === "paid" && state.order && (
        <div className="mt-10 bg-surface p-8 rounded-sm text-left max-w-md mx-auto">
          <div className="text-xs tracking-wider-2 uppercase text-sage mb-2">Order</div>
          <div className="font-display text-2xl text-darkRoast mb-4" data-testid="order-id">#{state.order.order_id.slice(-8).toUpperCase()}</div>
          <div className="space-y-2 text-sm">
            {state.order.items.map((it) => (
              <div key={it.product_id} className="flex justify-between">
                <span className="text-earth">{it.name} × {it.quantity}</span>
                <span>${(it.unit_price * it.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-darkRoast/10 mt-4 pt-4 flex justify-between font-medium">
            <span>Total</span><span>${state.order.total.toFixed(2)}</span>
          </div>
          {state.order.fulfillment === "pickup" && (
            <div className="mt-4 text-sm text-earth bg-cream rounded-sm p-3" data-testid="pickup-confirmation">
              <span className="text-darkRoast">Local pickup in {state.order.pickup_location || "Plymouth, CA"}.</span> We'll reach out by email to arrange a time.
            </div>
          )}
        </div>
      )}
      <div className="mt-10 flex justify-center gap-4">
        <Link to="/shop" className="bg-darkRoast text-cream rounded-full px-6 py-3 text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors">
          Keep shopping
        </Link>
        <Link to="/profile" className="border border-darkRoast/20 text-darkRoast rounded-full px-6 py-3 text-xs tracking-wider-2 uppercase hover:border-sage">
          View orders
        </Link>
      </div>
    </div>
  );
}
